import { useSyncExternalStore } from 'react';
import { readJson, readStorage, writeStorage } from '@/lib/storage';

/**
 * Small per-viewer UI state shared by the shell: sidebar collapse and the
 * open sidebar groups (both persisted), command palette and shortcuts
 * dialog visibility.
 */
interface UiState {
  sidebarCollapsed: boolean;
  /** Ids of the sidebar groups the viewer opened. */
  navOpen: readonly string[];
  paletteOpen: boolean;
  shortcutsOpen: boolean;
}

const NAV_OPEN_KEY = 'biller.sidebar.groups';

const isStringList = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((entry) => typeof entry === 'string');

let state: UiState = {
  sidebarCollapsed: readStorage('local', 'biller.sidebar.collapsed') === '1',
  navOpen: readJson('local', NAV_OPEN_KEY, isStringList) ?? [],
  paletteOpen: false,
  shortcutsOpen: false,
};

const listeners = new Set<() => void>();

function set(patch: Partial<UiState>): void {
  state = { ...state, ...patch };
  for (const listener of listeners) listener();
}

function setNavOpen(navOpen: string[]): void {
  writeStorage('local', NAV_OPEN_KEY, JSON.stringify(navOpen));
  set({ navOpen });
}

export const ui = {
  toggleSidebar: (): void => {
    const next = !state.sidebarCollapsed;
    writeStorage('local', 'biller.sidebar.collapsed', next ? '1' : '0');
    set({ sidebarCollapsed: next });
  },
  toggleNavGroup: (id: string): void => {
    setNavOpen(
      state.navOpen.includes(id) ? state.navOpen.filter((open) => open !== id) : [...state.navOpen, id],
    );
  },
  setPaletteOpen: (open: boolean): void => {
    set({ paletteOpen: open });
  },
  setShortcutsOpen: (open: boolean): void => {
    set({ shortcutsOpen: open });
  },
};

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useUi(): UiState {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => state,
  );
}
